// Pullman Reports → Pullman Live (2026-11-03). What the crew sold on the Pullman Live
// iPads: by service, then by iPad, then the lines themselves (drill-down), with quantity
// and value at every level. The Stock report download is a REPLEN report by default
// ("only sales not previously downloaded", always pre-ticked): each download marks the
// lines it covered, so the next one picks up where it left off. Untick it and a period
// picker appears for a plain "what sold between these dates" workbook.
// Data: /api/dashboards/pullman/live/sales (api/src/functions/pullmanLiveSales.ts).
import { Fragment, useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'

interface DeviceSales { device_id: string; device_name: string; crew_names: string; lines: number; qty: number; value: number; undownloaded: number }
interface ServiceSales { shd_rec_no: number; service_date: string; departure_time: string; service_type: string; service_description: string; lines: number; qty: number; value: number; undownloaded: number; devices: DeviceSales[] }
interface DeviceTotal { device_id: string; device_name: string; lines: number; qty: number; value: number }
interface Download { id: number; downloaded_at: string; downloaded_by: string | null; mode: string; period_from: string | null; period_to: string | null; line_count: number; qty_total: number; value_total: number }
interface LastService { shd_rec_no: number; service_date: string; departure_time: string; service_type: string; pending_qty: number; pending_value: number; qty: number; value: number }
interface Payload { from: string; to: string; last_service: LastService | null; totals: { lines: number; qty: number; value: number; services: number; ipads: number }; services: ServiceSales[]; devices: DeviceTotal[]; pending: { lines: number; qty: number; value: number; oldest: string | null }; downloads: Download[] }
interface Line { uuid: string; booking_ref: number; account_name: string; carriages: string; part_no: string | null; description: string; unit_price: number; qty: number; value: number; status: string; crew_name: string | null; taken_at: string | null; downloaded_at: string | null; download_batch: number | null }

const GREEN = '#0b3d2e'
const num = (n: number) => n.toLocaleString('en-GB')
const gbp = (n: number) => `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const dmy = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
const hm = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) : '—')
const TH: CSSProperties = { textAlign: 'left', padding: '6px 8px', color: '#6b6b6b', fontWeight: 600, fontSize: '0.72rem', borderBottom: '1px solid #e5e1d6', whiteSpace: 'nowrap' }
const TD: CSSProperties = { padding: '6px 8px', fontSize: '0.84rem', borderBottom: '1px solid #f0ece1', verticalAlign: 'top' }
const R: CSSProperties = { textAlign: 'right' }
const btn: CSSProperties = { font: 'inherit', fontSize: '0.82rem', fontWeight: 600, padding: '0.45rem 0.9rem', borderRadius: 4, cursor: 'pointer', border: `1px solid ${GREEN}`, background: '#fff', color: GREEN, textDecoration: 'none', display: 'inline-block' }
const btnPrimary: CSSProperties = { ...btn, background: GREEN, color: '#fff' }
const card: CSSProperties = { background: '#fff', border: '1px solid #e5e1d6', borderRadius: 8, padding: '0.75rem 1rem' }
const kpi: CSSProperties = { ...card, minWidth: 140 }
const kpiK: CSSProperties = { fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6b6b6b' }
const kpiV: CSSProperties = { fontSize: '1.35rem', fontWeight: 700, color: GREEN, marginTop: 2 }

const pad = (n: number) => String(n).padStart(2, '0')
const isoOf = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
function todayUtc(): Date { const n = new Date(); return new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate())) }
function seasonStartFor(d: Date): string { const y = d.getUTCMonth() + 1 >= 3 ? d.getUTCFullYear() : d.getUTCFullYear() - 1; return `${y}-03-01` }
type Pick = 'today' | 'last7' | 'month' | 'season' | 'custom'
function pickRange(p: Exclude<Pick, 'custom'>): [string, string] {
  const t = todayUtc()
  if (p === 'today') return [isoOf(t), isoOf(t)]
  if (p === 'last7') return [isoOf(new Date(t.getTime() - 6 * 86_400_000)), isoOf(t)]
  if (p === 'month') return [`${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-01`, isoOf(t)]
  return [seasonStartFor(t), isoOf(t)]
}

function RangePicker({ pick, from, to, onPick, onRange }: { pick: Pick; from: string; to: string; onPick: (p: Pick) => void; onRange: (f: string, t: string) => void }) {
  const picks: { key: Pick; label: string }[] = [{ key: 'today', label: 'Today' }, { key: 'last7', label: 'Last 7 days' }, { key: 'month', label: 'This month' }, { key: 'season', label: 'Season' }, { key: 'custom', label: 'Custom' }]
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      {picks.map((p) => (
        <button key={p.key} type="button" className={`dashboard-pill ${pick === p.key ? 'active' : ''}`} onClick={() => onPick(p.key)}>{p.label}</button>
      ))}
      {pick === 'custom' && (
        <>
          <input type="date" value={from} max={to} onChange={(e) => onRange(e.target.value, to)} style={{ font: 'inherit', fontSize: '0.82rem', padding: '0.3rem' }} />
          <span style={{ color: '#6b6b6b' }}>to</span>
          <input type="date" value={to} min={from} onChange={(e) => onRange(from, e.target.value)} style={{ font: 'inherit', fontSize: '0.82rem', padding: '0.3rem' }} />
        </>
      )}
    </div>
  )
}

export function PullmanLiveSales() {
  const [pick, setPick] = useState<Pick>('season')
  const [[from, to], setRange] = useState<[string, string]>(() => pickRange('season'))
  const [data, setData] = useState<Payload | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openSvc, setOpenSvc] = useState<Set<number>>(new Set())
  const [openDev, setOpenDev] = useState<Set<string>>(new Set())
  const [lines, setLines] = useState<Record<string, Line[] | 'loading' | 'error'>>({})
  // Stock report controls
  const [newOnly, setNewOnly] = useState(true)
  const [scope, setScope] = useState<'all' | 'last'>('all')
  const [dlPick, setDlPick] = useState<Pick>('month')
  const [[dlFrom, dlTo], setDlRange] = useState<[string, string]>(() => pickRange('month'))
  const [reloadKey, setReloadKey] = useState(0)

  const choose = (p: Pick) => { setPick(p); if (p !== 'custom') setRange(pickRange(p)) }
  const chooseDl = (p: Pick) => { setDlPick(p); if (p !== 'custom') setDlRange(pickRange(p)) }

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const res = await fetch(`/api/dashboards/pullman/live/sales?from=${from}&to=${to}`, { credentials: 'same-origin' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setData((await res.json()) as Payload)
    } catch (e) {
      setError(String((e as Error)?.message ?? e))
    } finally {
      setLoading(false)
    }
  }, [from, to])
  useEffect(() => { void load() }, [load, reloadKey])

  const toggleSvc = (shd: number) => setOpenSvc((s) => { const n = new Set(s); n.has(shd) ? n.delete(shd) : n.add(shd); return n })
  const toggleDev = async (shd: number, device: string) => {
    const key = `${shd}|${device}`
    setOpenDev((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n })
    if (!lines[key]) {
      setLines((l) => ({ ...l, [key]: 'loading' }))
      try {
        const res = await fetch(`/api/dashboards/pullman/live/sales/lines?shd=${shd}&device=${encodeURIComponent(device)}`, { credentials: 'same-origin' })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const j = (await res.json()) as { lines: Line[] }
        setLines((l) => ({ ...l, [key]: j.lines }))
      } catch {
        setLines((l) => ({ ...l, [key]: 'error' }))
      }
    }
  }

  const dlHref = newOnly
    ? `/api/dashboards/pullman/live/stock.xlsx?mode=${scope === 'last' ? 'last' : 'new'}`
    : `/api/dashboards/pullman/live/stock.xlsx?mode=period&from=${dlFrom}&to=${dlTo}`
  const lastReplen = useMemo(() => data?.downloads.find((d) => d.mode !== 'period') ?? null, [data])
  const last = data?.last_service ?? null
  const nothingToDownload = newOnly && !!data && (scope === 'last' ? !last || last.pending_qty === 0 : data.pending.lines === 0)

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Stock report */}
      <div style={{ ...card, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 220 }}>
          <span style={kpiK}>Stock report</span>
          <span style={{ fontSize: '0.8rem', color: '#6b6b6b' }}>
            {data ? (data.pending.qty > 0 ? `${num(data.pending.qty)} item${data.pending.qty === 1 ? '' : 's'} sold since the last stock report (${gbp(data.pending.value)})` : 'Nothing new since the last stock report') : '…'}
            {lastReplen ? ` · last replen ${hm(lastReplen.downloaded_at)}` : ''}
          </span>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={newOnly} onChange={(e) => setNewOnly(e.target.checked)} style={{ width: 18, height: 18 }} />
          Only download new sales that have not been previously downloaded
        </label>
        {newOnly && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className={`dashboard-pill ${scope === 'all' ? 'active' : ''}`} onClick={() => setScope('all')}>All new sales</button>
            <button type="button" className={`dashboard-pill ${scope === 'last' ? 'active' : ''}`} onClick={() => setScope('last')} disabled={!last} title={last ? `${dmy(last.service_date)} ${last.departure_time} ${last.service_type}` : 'No service with iPad sales yet'}>
              Last service only{last ? ` · ${dmy(last.service_date).replace(/^\w+ /, '')} ${last.departure_time}` : ''}
            </button>
            {scope === 'last' && last && (
              <span style={{ fontSize: '0.8rem', color: '#6b6b6b' }}>{last.service_type}: {num(last.pending_qty)} item{last.pending_qty === 1 ? '' : 's'} not yet downloaded ({gbp(last.pending_value)}) of {num(last.qty)} sold</span>
            )}
          </div>
        )}
        {!newOnly && <RangePicker pick={dlPick} from={dlFrom} to={dlTo} onPick={chooseDl} onRange={(f, t) => setDlRange([f, t])} />}
        <a
          href={nothingToDownload ? undefined : dlHref}
          style={{ ...btnPrimary, marginLeft: 'auto', opacity: nothingToDownload ? 0.5 : 1, pointerEvents: nothingToDownload ? 'none' : undefined }}
          download
          onClick={() => { setTimeout(() => setReloadKey((k) => k + 1), 2500) }}
          title={newOnly ? (scope === 'last' ? "The last service's un-downloaded lines; they are marked downloaded" : 'Every line in the workbook is marked downloaded, so the next report starts after it') : `Sales on services ${dmy(dlFrom)} – ${dmy(dlTo)}; nothing is marked`}
        >
          Download XLS Stock report{newOnly ? (scope === 'last' ? ' (last service replen)' : ' (replen)') : ''}
        </a>
      </div>

      {/* Range + KPIs */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <RangePicker pick={pick} from={from} to={to} onPick={choose} onRange={(f, t) => setRange([f, t])} />
        <span style={{ fontSize: '0.8rem', color: '#6b6b6b' }}>Services {dmy(from)} – {dmy(to)}</span>
      </div>
      {error && <p style={{ color: '#a33' }}>Couldn&apos;t load: {error}</p>}
      {data && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={kpi}><div style={kpiK}>Sales</div><div style={kpiV}>{gbp(data.totals.value)}</div></div>
          <div style={kpi}><div style={kpiK}>Items</div><div style={kpiV}>{num(data.totals.qty)}</div></div>
          <div style={kpi}><div style={kpiK}>Lines</div><div style={kpiV}>{num(data.totals.lines)}</div></div>
          <div style={kpi}><div style={kpiK}>Services</div><div style={kpiV}>{num(data.totals.services)}</div></div>
          <div style={kpi}><div style={kpiK}>iPads</div><div style={kpiV}>{num(data.totals.ipads)}</div></div>
        </div>
      )}

      {/* By iPad (range) */}
      {data && data.devices.length > 0 && (
        <div style={card}>
          <div style={{ ...kpiK, marginBottom: 6 }}>By iPad · {dmy(from)} – {dmy(to)}</div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr><th style={TH}>iPad</th><th style={{ ...TH, ...R }}>Items</th><th style={{ ...TH, ...R }}>Lines</th><th style={{ ...TH, ...R }}>Sales</th></tr></thead>
            <tbody>
              {data.devices.map((d) => (
                <tr key={d.device_id || 'unknown'}><td style={TD}>{d.device_name}</td><td style={{ ...TD, ...R }}>{num(d.qty)}</td><td style={{ ...TD, ...R }}>{num(d.lines)}</td><td style={{ ...TD, ...R, fontWeight: 600 }}>{gbp(d.value)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* By service → iPad → lines */}
      <div style={card}>
        <div style={{ ...kpiK, marginBottom: 6 }}>By service and iPad — tap a service, then an iPad, for the lines</div>
        {loading && !data && <p>Loading…</p>}
        {data && data.services.length === 0 && <p style={{ color: '#6b6b6b' }}>No iPad sales on services in this range.</p>}
        {data && data.services.length > 0 && (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr><th style={TH}></th><th style={TH}>Service</th><th style={TH}>iPads · crew</th><th style={{ ...TH, ...R }}>Items</th><th style={{ ...TH, ...R }}>Lines</th><th style={{ ...TH, ...R }}>Sales</th><th style={{ ...TH, ...R }}>Not yet downloaded</th></tr>
            </thead>
            <tbody>
              {data.services.map((s) => {
                const open = openSvc.has(s.shd_rec_no)
                return (
                  <Fragment key={s.shd_rec_no}>
                    <tr onClick={() => toggleSvc(s.shd_rec_no)} style={{ cursor: 'pointer', background: open ? '#f6f3ea' : undefined }}>
                      <td style={{ ...TD, width: 24, color: GREEN, fontWeight: 700 }}>{open ? '▾' : '▸'}</td>
                      <td style={TD}><b>{dmy(s.service_date)}</b> · {s.departure_time} · {s.service_type}</td>
                      <td style={TD}>{s.devices.length} iPad{s.devices.length === 1 ? '' : 's'}</td>
                      <td style={{ ...TD, ...R }}>{num(s.qty)}</td>
                      <td style={{ ...TD, ...R }}>{num(s.lines)}</td>
                      <td style={{ ...TD, ...R, fontWeight: 600 }}>{gbp(s.value)}</td>
                      <td style={{ ...TD, ...R, color: s.undownloaded ? '#8a5a12' : '#6b6b6b' }}>{s.undownloaded ? num(s.undownloaded) : '—'}</td>
                    </tr>
                    {open && s.devices.map((d) => {
                      const key = `${s.shd_rec_no}|${d.device_id}`
                      const dOpen = openDev.has(key)
                      const ls = lines[key]
                      return (
                        <Fragment key={key}>
                          <tr onClick={() => void toggleDev(s.shd_rec_no, d.device_id)} style={{ cursor: 'pointer', background: '#fbfaf6' }}>
                            <td style={TD}></td>
                            <td style={{ ...TD, paddingLeft: 28, color: GREEN, fontWeight: 600 }}>{dOpen ? '▾' : '▸'} {d.device_name}</td>
                            <td style={TD}>{d.crew_names || '—'}</td>
                            <td style={{ ...TD, ...R }}>{num(d.qty)}</td>
                            <td style={{ ...TD, ...R }}>{num(d.lines)}</td>
                            <td style={{ ...TD, ...R, fontWeight: 600 }}>{gbp(d.value)}</td>
                            <td style={{ ...TD, ...R, color: d.undownloaded ? '#8a5a12' : '#6b6b6b' }}>{d.undownloaded ? num(d.undownloaded) : '—'}</td>
                          </tr>
                          {dOpen && (
                            <tr>
                              <td style={TD}></td>
                              <td colSpan={6} style={{ ...TD, paddingLeft: 28, paddingBottom: 12 }}>
                                {ls === 'loading' && <span style={{ color: '#6b6b6b' }}>Loading lines…</span>}
                                {ls === 'error' && <span style={{ color: '#a33' }}>Couldn&apos;t load the lines.</span>}
                                {Array.isArray(ls) && (
                                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                    <thead><tr><th style={TH}>Taken</th><th style={TH}>Table / booking</th><th style={TH}>Item</th><th style={{ ...TH, ...R }}>Qty</th><th style={{ ...TH, ...R }}>Unit</th><th style={{ ...TH, ...R }}>Value</th><th style={TH}>Crew</th><th style={TH}>Downloaded</th></tr></thead>
                                    <tbody>
                                      {ls.map((l) => (
                                        <tr key={l.uuid}>
                                          <td style={TD}>{hm(l.taken_at)}</td>
                                          <td style={TD}>{l.account_name} <span style={{ color: '#6b6b6b' }}>#{l.booking_ref}{l.carriages ? ` · ${l.carriages}` : ''}</span></td>
                                          <td style={TD}>{l.description}</td>
                                          <td style={{ ...TD, ...R }}>{num(l.qty)}</td>
                                          <td style={{ ...TD, ...R }}>{gbp(l.unit_price)}</td>
                                          <td style={{ ...TD, ...R, fontWeight: 600 }}>{gbp(l.value)}</td>
                                          <td style={TD}>{l.crew_name ?? '—'}</td>
                                          <td style={TD}>{l.downloaded_at ? `${hm(l.downloaded_at)} · batch ${l.download_batch}` : <span style={{ color: '#8a5a12' }}>not yet</span>}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      )
                    })}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Download history */}
      {data && data.downloads.length > 0 && (
        <div style={card}>
          <div style={{ ...kpiK, marginBottom: 6 }}>Stock report downloads</div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr><th style={TH}>When</th><th style={TH}>By</th><th style={TH}>Mode</th><th style={TH}>Period</th><th style={{ ...TH, ...R }}>Lines</th><th style={{ ...TH, ...R }}>Items</th><th style={{ ...TH, ...R }}>Value</th></tr></thead>
            <tbody>
              {data.downloads.map((d) => (
                <tr key={d.id}>
                  <td style={TD}>{hm(d.downloaded_at)} <span style={{ color: '#6b6b6b' }}>· batch {d.id}</span></td>
                  <td style={TD}>{d.downloaded_by ?? '—'}</td>
                  <td style={TD}>{d.mode === 'new' ? 'New sales only (replen)' : d.mode === 'last' ? 'Last service only (replen)' : 'Period'}</td>
                  <td style={TD}>{d.period_from && d.period_to ? (d.period_from === d.period_to ? dmy(d.period_from) : `${dmy(d.period_from)} – ${dmy(d.period_to)}`) : '—'}</td>
                  <td style={{ ...TD, ...R }}>{num(d.line_count)}</td>
                  <td style={{ ...TD, ...R }}>{num(d.qty_total)}</td>
                  <td style={{ ...TD, ...R }}>{gbp(d.value_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
